import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listTickets } from '../../services/tickets'
import { getUser, logout } from '../../services/helpers'
import MobileBottomNav from '../../components/MobileBottomNav'
import TicketTemplate from '../../components/TicketTemplate'
import { useReactToPrint } from 'react-to-print'
import { 
  TicketIcon, 
  CalendarIcon, 
  MapPinIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  QrCodeIcon,
  ArrowDownTrayIcon,
  TagIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BuildingOffice2Icon,
  EyeIcon
} from '@heroicons/react/24/outline'

const TicketsPage = () => {
  const navigate = useNavigate()
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, active, used
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [viewTicketModal, setViewTicketModal] = useState(false)
  const [ticketForView, setTicketForView] = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [user, setUser] = useState(null)
  const ticketTemplateRef = useRef()
  const displayName = user?.name || user?.email || 'Attendee'

  useEffect(() => {
    const u = getUser()
    setUser(u)
  }, [])

  useEffect(() => {
    fetchTickets()
  }, [])

  const fetchTickets = async () => {
    setLoading(true)
    const res = await listTickets()
    if (res.status === 200) {
      setTickets(res.data || [])
    }
    setLoading(false)
  }

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'active') return ticket.status === 'ACTIVE'
    if (filter === 'used') return ticket.status === 'USED'
    return true
  })

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-50 border-green-200'
      case 'USED': return 'text-gray-600 bg-gray-50 border-gray-200'
      case 'TRANSFERRED': return 'text-blue-600 bg-blue-50 border-blue-200'
      case 'VOID': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getTypeColor = (type) => {
    return type === 'EVENT' 
      ? 'text-purple-600 bg-purple-50 border-purple-200'
      : 'text-emerald-600 bg-emerald-50 border-emerald-200'
  }

  const getTicketType = (ticket) => {
    return ticket.eventId ? 'EVENT' : 'OFFER'
  }

  const getTicketPrice = (ticket) => {
    if (ticket.ticketTypeId?.price) {
      return ticket.ticketTypeId.price
    }
    if (ticket.offerId?.discountedPrice) {
      return ticket.offerId.discountedPrice
    }
    return 0
  }

  const handleViewTicket = (e, ticket) => {
    e.preventDefault()
    e.stopPropagation()
    setTicketForView(ticket)
    setViewTicketModal(true)
  }

  const handlePrint = useReactToPrint({
    content: () => ticketTemplateRef.current,
    documentTitle: `Ticket-${ticketForView?.code || 'ticket'}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        body {
          margin: 0;
          padding: 0;
        }
        .print\\:hidden {
          display: none !important;
        }
        #ticket-template {
          margin: 0 !important;
          padding: 0 !important;
          max-width: 100% !important;
          transform: scale(0.85);
          transform-origin: top left;
        }
      }
    `
  })

  const tabs = [
    { id: 'events', label: 'Discover Events', icon: CalendarIcon, path: '/events' },
    { id: 'offers', label: 'Browse Offers', icon: TagIcon, path: '/offers' },
    { id: 'centers', label: 'Centers', icon: BuildingOffice2Icon, path: '/centers' },
    { id: 'tickets', label: 'My Tickets', icon: TicketIcon, path: '/account/tickets' },
    { id: 'orders', label: 'Order History', icon: ShoppingBagIcon, path: '/account/orders' },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/account/disputes' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/account/profile' }
  ]

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation - Hidden on mobile, shown on desktop */}
      <aside className={`hidden lg:block fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-105">
                <TicketIcon className="w-5 h-5" />
              </div>
              <span className="text-xl font-bold text-slate-900 tracking-tight">Obule</span>
            </Link>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const currentPath = window.location.pathname
              const isActive = tab.id === 'centers' 
                ? currentPath.startsWith('/centers')
                : currentPath === tab.path
              
  return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/auth/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col pb-20 lg:pb-0">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-center px-4 h-16 sticky top-0 z-40">
          <span className="font-semibold text-slate-900">My Tickets</span>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-2">My Tickets</h1>
              <p className="text-slate-600">View and manage your event and offer tickets</p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-8">
          {[
            { id: 'all', label: 'All Tickets', count: tickets.length },
            { id: 'active', label: 'Active', count: tickets.filter(t => t.status === 'ACTIVE').length },
            { id: 'used', label: 'Used', count: tickets.filter(t => t.status === 'USED').length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                filter === tab.id
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {tab.label} <span className="ml-2 opacity-75">({tab.count})</span>
            </button>
          ))}
            </div>

            {/* Tickets Grid */}
            {loading ? (
          <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
                <p className="mt-4 text-slate-600">Loading your tickets...</p>
              </div>
            </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-20">
                <TicketIcon className="mx-auto h-24 w-24 text-slate-300" />
                <h3 className="mt-6 text-2xl font-semibold text-slate-900">No tickets found</h3>
                <p className="mt-2 text-slate-600">
                  {filter === 'all' 
                    ? "You haven't purchased any tickets yet. Browse events and offers to get started!"
                    : `No ${filter} tickets found.`}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTickets.map(ticket => (
              <div
                key={ticket._id}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-200"
              >
                {/* Ticket Header - Gradient Background */}
                <div className={`relative p-6 ${
                  getTicketType(ticket) === 'EVENT' 
                    ? 'bg-gradient-to-br from-purple-500 to-purple-600' 
                    : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                }`}>
                  {/* Type Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full border border-white/30">
                      {getTicketType(ticket)}
                    </span>
                  </div>

                  {/* Event/Offer Icon */}
                  <div className="mb-4">
                    {getTicketType(ticket) === 'EVENT' ? (
                      <CalendarIcon className="w-12 h-12 text-white/90" />
                    ) : (
                      <TagIcon className="w-12 h-12 text-white/90" />
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-2 line-clamp-2">
                    {ticket.eventId?.title || ticket.offerId?.title || 'Ticket'}
                  </h3>

                  {/* Ticket Code & Price */}
                  <div className="flex items-center justify-between text-white/90">
                    <div className="flex items-center gap-2">
                      <QrCodeIcon className="w-5 h-5" />
                      <span className="font-mono text-sm font-medium">{ticket.code}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">₦{getTicketPrice(ticket).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Ticket Body */}
                <div className="p-6">
                  {/* Status Badge */}
                  <div className="mb-4">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${getStatusColor(ticket.status)}`}>
                      {ticket.status === 'ACTIVE' ? (
                        <CheckCircleIcon className="w-5 h-5" />
                      ) : (
                        <XCircleIcon className="w-5 h-5" />
                      )}
                      {ticket.status}
                    </span>
                  </div>

                  {/* Event/Offer Details */}
                  {getTicketType(ticket) === 'EVENT' && ticket.eventId && (
                    <div className="space-y-3 mb-6">
                      {ticket.eventId.startsAt && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <CalendarIcon className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium">
                              {new Date(ticket.eventId.startsAt).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(ticket.eventId.startsAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                      {ticket.eventId.venue && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <MapPinIcon className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm">{ticket.eventId.venue}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Offer Details */}
                  {getTicketType(ticket) === 'OFFER' && ticket.offerId && (
                    <div className="space-y-3 mb-6">
                      {ticket.offerId.validFrom && ticket.offerId.validUntil && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <CalendarIcon className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Valid Period</p>
                            <p className="text-xs text-slate-500">
                              {new Date(ticket.offerId.validFrom).toLocaleDateString()} - {new Date(ticket.offerId.validUntil).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      )}
                      {ticket.offerId.normalPrice && (
                        <div className="flex items-start gap-3 text-slate-700">
                          <TagIcon className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium">
                              You saved ₦{(ticket.offerId.normalPrice - ticket.offerId.discountedPrice).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-500 line-through">
                              Normal price: ₦{ticket.offerId.normalPrice.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ticket Type Name */}
                  {ticket.ticketTypeId?.name && (
                    <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Ticket Type</p>
                      <p className="text-sm font-semibold text-slate-900">{ticket.ticketTypeId.name}</p>
                    </div>
                  )}

                  {/* Issued Date */}
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-6">
                    <ClockIcon className="w-4 h-4" />
                    <span>
                      Issued {new Date(ticket.issuedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={(e) => handleViewTicket(e, ticket)}
                      className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${
                        getTicketType(ticket) === 'EVENT'
                          ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/30'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/30'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <EyeIcon className="w-5 h-5" />
                        View Ticket
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSelectedTicket(ticket)
                      }}
                      className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all duration-200"
                      title="View QR Code"
                    >
                      <QrCodeIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Decorative Perforations */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-6 h-6 bg-slate-50 rounded-full -ml-3"></div>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-6 h-6 bg-slate-50 rounded-full -mr-3"></div>
              </div>
            ))}
              </div>
            )}

            {/* View Ticket Modal */}
            {viewTicketModal && ticketForView && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                <div className="bg-slate-50 rounded-2xl shadow-2xl max-w-4xl w-full my-8">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-white rounded-t-2xl">
                    <h2 className="text-2xl font-bold text-slate-900">Ticket Details</h2>
                    <button
                      onClick={() => {
                        setViewTicketModal(false)
                        setTicketForView(null)
                      }}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <XCircleIcon className="w-8 h-8" />
                    </button>
                  </div>

                  {/* Ticket Template */}
                  <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                    <div ref={ticketTemplateRef}>
                      <TicketTemplate 
                        ticket={ticketForView} 
                        user={user}
                        onPrint={handlePrint}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* QR Code Modal */}
            {selectedTicket && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Modal Header */}
              <div className={`p-6 ${
                getTicketType(selectedTicket) === 'EVENT'
                  ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                  : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
              }`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {selectedTicket.eventId?.title || selectedTicket.offerId?.title}
                    </h3>
                    <p className="text-white/80 text-sm">Present this QR code at entry</p>
                  </div>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="text-white/80 hover:text-white"
                  >
                    <XCircleIcon className="w-8 h-8" />
                  </button>
                </div>
              </div>

              {/* QR Code */}
              <div className="p-8">
                <div className="bg-white border-4 border-slate-200 rounded-2xl p-6 mb-6">
                  <div className="aspect-square bg-slate-100 rounded-xl flex items-center justify-center">
                    <QrCodeIcon className="w-32 h-32 text-slate-300" />
                    <p className="absolute text-xs text-slate-400 mt-40">QR Code: {selectedTicket.code}</p>
                  </div>
                </div>

                {/* Ticket Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Ticket Code</span>
                    <span className="font-mono font-semibold text-slate-900">{selectedTicket.code}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Status</span>
                    <span className={`font-semibold ${
                      selectedTicket.status === 'ACTIVE' ? 'text-green-600' : 'text-gray-600'
                    }`}>
                      {selectedTicket.status}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Issued</span>
                    <span className="font-medium text-slate-900">
                      {new Date(selectedTicket.issuedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="w-full px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors"
                >
                  Close
                </button>
              </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <MobileBottomNav />
    </div>
  )
}

export default TicketsPage

